import { DayReportFieldGroups, EntityType, FieldType } from '../../app/services/entities/entity-type.class';
import { AutocompleteType } from '../../app/services/planning/autocomplete.service';
import { Roles } from './roles';
import { PlanningStatus } from './status.planning';

export const basePlanningFields = {
    status_key: {
        name: 'Status',
        type: FieldType.StatusKey,
        required: true,
        default: PlanningStatus.PlanningReservering,
        displayOrder: 0,
        displayPercent: 12,
        disableRules: [
            {
                field: 'status_key',
                hasValue: [PlanningStatus.PlanningBehoefte],
                disableFields: ['begindate', 'enddate'],
            },
            {
                field: 'status_key',
                hasValue: [PlanningStatus.PlanningGeblokkeerd],
                disableFields: [
                    'location',
                    'behoefteThermoM1',
                    'behoefteThermoM2',
                    'behoefteOverigM1',
                    'behoefteOverigM2',
                    'behoefteUren',
                    'behoefteM1',
                    'behoefteM2',
                    'contractor',
                    'afzetting',
                    'materiaal',
                    'verkeersmaatregel',
                    'behoefteInventarisatie',
                ],
            },
        ],
        atLeastOneRequiredRules: [
            {
                field: 'status_key',
                hasValue: [PlanningStatus.PlanningBehoefte],
                requiredFields: [
                    'behoefteThermoM1',
                    'behoefteThermoM2',
                    'behoefteOverigM1',
                    'behoefteOverigM2',
                    'behoefteUren',
                    'behoefteM1',
                    'behoefteM2',
                ],
            },
        ],
        statusType: 'planning',
    },
    afas_project_id: {
        displayOrder: 2,
        displayPercent: 18,
        name: 'Project',
        type: FieldType.ProjectId,
        validators: {
            maxLength: 3000,
        },
        requiredRules: [
            {
                field: 'status_key',
                hasValue: [PlanningStatus.PlanningGepland, PlanningStatus.PlanningRegen],
            },
        ],
    },
    contractor: {
        name: 'Opdrachtgever',
        type: FieldType.Text,
        autocomplete: AutocompleteType.contractors,
        displayOrder: 1,
        displayPercent: 20,
        validators: {
            maxLength: 3000,
        },
    },
    afas_customer_id: {
        displayOrder: 1,
        displayPercent: 20,
        name: 'Opdrachtgever',
        type: FieldType.CustomerId,
        requiredRules: [
            {
                field: 'status_key',
                hasValue: [
                    PlanningStatus.PlanningReservering,
                    PlanningStatus.PlanningBehoefte,
                    PlanningStatus.PlanningGepland,
                    PlanningStatus.PlanningRegen,
                ],
            },
        ],
    },
    location: {
        name: 'Locatie',
        extraFields: ['lat', 'lng', 'location_data'],
        displayOrder: 3,
        displayPercent: 20,
        type: FieldType.Location,
        validators: {
            maxLength: 3000,
        },
        requiredRules: [
            {
                field: 'status_key',
                hasValue: [
                    PlanningStatus.PlanningReservering,
                    PlanningStatus.PlanningBehoefte,
                    PlanningStatus.PlanningGepland,
                    PlanningStatus.PlanningRegen,
                ],
            },
        ],
    },
    performer: {
        name: 'Voorman',
        type: FieldType.UserIdWithLeaves,
        dataFilter: [
            Roles.Foreman,
            Roles.EmployeeBlast,
            Roles.EmployeeInventorize,
            Roles.EmployeeMarkings,
            Roles.EmployeeWipe,
        ],
        displayOrder: 7,
        displayPercent: 15,
        requiredRules: [
            {
                field: 'status_key',
                hasValue: [PlanningStatus.PlanningGepland, PlanningStatus.PlanningRegen],
            },
        ],
    },
    projectmanager: {
        name: 'Projectleider',
        type: FieldType.UserId,
        displayOrder: 8,
        displayPercent: 15,
        dataFilter: [Roles.Projectmanager],
        requiredRules: [
            {
                field: 'status_key',
                hasValue: [PlanningStatus.PlanningGepland, PlanningStatus.PlanningRegen],
            },
        ],
    },
    comment: {
        displayOrder: 9,
        displayPercent: 20,
        name: 'Opmerking',
        type: FieldType.Textarea,
        validators: {
            maxLength: 3000,
        },
    },
    repetition: {
        name: 'Herhalen',
        type: FieldType.Checkbox,
        displayOrder: 11,
        displayPercent: 15,
        default: false,
    },
    repetitionEndDate: {
        name: 'Tot en met',
        type: FieldType.Date,
        displayOrder: 12,
        displayPercent: 15,
        validators: {
            maxDate: new Date(new Date().getFullYear(), new Date().getMonth() + 12, 1, 0, 0, 0, 0), // 1 year in the future
        },
    },
    nrOfRepetitions: {
        name: 'Aantal herhalingen',
        type: FieldType.Number,
        displayOrder: 13,
        displayPercent: 10,
        validators: {
            min: 2,
            max: 260,
        },
    },
    interval_week: {
        name: 'Week interval',
        type: FieldType.Number,
        displayOrder: 14,
        displayPercent: 10,
        default: 1,
        validators: {
            min: 1,
            max: 52,
        },
        requiredRules: [
            {
                field: 'repetition',
                hasValue: [true],
            },
        ],
    },
    weekdays: {
        name: 'Op dagen',
        type: FieldType.Select,
        displayOrder: 15,
        displayPercent: 15,
        options: [
            { key: 'repeat_monday', name: 'Maandag' },
            { key: 'repeat_tuesday', name: 'Dinsdag' },
            { key: 'repeat_wednesday', name: 'Woensdag' },
            { key: 'repeat_thursday', name: 'Donderdag' },
            { key: 'repeat_friday', name: 'Vrijdag' },
            { key: 'repeat_saturday', name: 'Zaterdag' },
            { key: 'repeat_sunday', name: 'Zondag' },
        ],
        multiple: true,
        requiredRules: [
            {
                field: 'repetition',
                hasValue: [true],
            },
        ],
    },
    datesLabel: {
        name: 'Resulterende datums',
        type: FieldType.ProgressDates,
        displayOrder: 16,
        displayPercent: 100,
        disabled: true,
        default: 'Geen herhaling',
    },
};

export const basePheFields = {
    begindate: {
        name: 'Begindatum',
        type: FieldType.DateTime,
        displayOrder: 5,
        displayPercent: 25,
        requiredRules: [
            {
                field: 'status_key',
                hasValue: [
                    PlanningStatus.PlanningReservering,
                    PlanningStatus.PlanningSlaapdag,
                    PlanningStatus.PlanningGeblokkeerd,
                    PlanningStatus.PlanningGepland,
                    PlanningStatus.PlanningRegen,
                ],
            },
        ],
    },
    enddate: {
        name: 'Einddatum',
        type: FieldType.DateTime,
        displayOrder: 6,
        displayPercent: 25,
        dataFilter: ['isEndDate'],
        requiredRules: [
            {
                field: 'status_key',
                hasValue: [
                    PlanningStatus.PlanningReservering,
                    PlanningStatus.PlanningSlaapdag,
                    PlanningStatus.PlanningGeblokkeerd,
                    PlanningStatus.PlanningGepland,
                    PlanningStatus.PlanningRegen,
                ],
            },
        ],
    },
};

const jaNeeOptions = [
    { key: 'ja', name: 'Ja' },
    { key: 'nee', name: 'Nee' },
];

export const baseDayReportFields: DayReportFieldGroups = {
    algemene_checks: {
        label: 'Algemene checks',
        fields: {
            pbm_juist: {
                name: 'PBM juist gebruikt',
                type: FieldType.Radio,
                options: jaNeeOptions,
                displayOrder: 1,
                default: 'ja',
            },
            opdrachtformulier_goed: {
                name: 'Opdrachtformulier goed',
                type: FieldType.Radio,
                options: jaNeeOptions,
                displayOrder: 2,
                default: 'ja',
            },
            verkeersveiligheid_goed: {
                name: 'Verkeersveiligheid goed',
                type: FieldType.Radio,
                options: jaNeeOptions,
                displayOrder: 3,
                default: 'ja',
            },
            inspectie_ondergrond_goed: {
                name: 'Inspectie ondergrond goed',
                type: FieldType.Radio,
                options: jaNeeOptions,
                displayOrder: 4,
                default: 'ja',
            },
            instelling_machine_goed: {
                name: 'Instelling machine goed',
                type: FieldType.Radio,
                options: jaNeeOptions,
                displayOrder: 5,
                default: 'ja',
            },
            verwerking_materiaal_goed: {
                name: 'Verwerking materiaal goed',
                type: FieldType.Radio,
                options: jaNeeOptions,
                displayOrder: 6,
                default: 'ja',
            },
            werking_machine_goed: {
                name: 'Werking machine goed',
                type: FieldType.Radio,
                options: jaNeeOptions,
                displayOrder: 7,
                default: 'ja',
            },
            applicatie_methode: {
                name: 'Applicatie methode',
                type: FieldType.Select,
                options: [
                    { key: 'thermoplast', name: 'Thermoplast' },
                    { key: 'sprayplast', name: 'Sprayplast' },
                    { key: 'koud_plastic', name: 'Koud plastic' },
                    { key: 'verf', name: 'Verf' },
                    { key: 'overig', name: 'Overig' },
                ],
                displayOrder: 8,
            },
        },
    },
    temp_thermoplast: {
        label: 'Temperatuur Thermoplast / Sprayplast',
        fields: {
            thermo_tijd_1: { name: 'Tijd', type: FieldType.BaseDateTime, displayOrder: 1 },
            thermo_moederketel_1: { name: 'Moederketel °C', type: FieldType.Number, displayOrder: 2 },
            thermo_machine_1: { name: 'Machine °C', type: FieldType.Number, displayOrder: 3 },
            thermo_slof_1: { name: 'Slof °C', type: FieldType.Number, displayOrder: 4 },
            thermo_tijd_2: { name: 'Tijd', type: FieldType.BaseDateTime, displayOrder: 5 },
            thermo_moederketel_2: { name: 'Moederketel °C', type: FieldType.Number, displayOrder: 6 },
            thermo_machine_2: { name: 'Machine °C', type: FieldType.Number, displayOrder: 7 },
            thermo_slof_2: { name: 'Slof °C', type: FieldType.Number, displayOrder: 8 },
        },
    },
    temp_vochtigheid: {
        label: 'Temperatuur / Luchtvochtigheid',
        fields: {
            vocht_tijd_1: { name: 'Tijd', type: FieldType.BaseDateTime, displayOrder: 1 },
            vocht_wegdek_1: { name: 'Wegdek °C', type: FieldType.Number, displayOrder: 2 },
            vocht_lucht_1: { name: 'Lucht °C', type: FieldType.Number, displayOrder: 3 },
            vocht_luchtvochtigheid_1: {
                name: 'Luchtvochtigheid %',
                type: FieldType.Number,
                validators: { min: 0, max: 100 },
                displayOrder: 4,
            },
            vocht_dauwpunt_1: { name: 'Dauwpunt °C', type: FieldType.Number, displayOrder: 5 },
            vocht_tijd_2: { name: 'Tijd', type: FieldType.BaseDateTime, displayOrder: 6 },
            vocht_wegdek_2: { name: 'Wegdek °C', type: FieldType.Number, displayOrder: 7 },
            vocht_lucht_2: { name: 'Lucht °C', type: FieldType.Number, displayOrder: 8 },
            vocht_luchtvochtigheid_2: {
                name: 'Luchtvochtigheid %',
                type: FieldType.Number,
                validators: { min: 0, max: 100 },
                displayOrder: 9,
            },
            vocht_dauwpunt_2: { name: 'Dauwpunt °C', type: FieldType.Number, displayOrder: 10 },
        },
    },
    maatvoering_strepen: {
        label: 'Maatvoering strepen',
        fields: {
            streep_lengte_1: {
                name: 'Lengte streep cm',
                type: FieldType.Number,
                validators: { min: 0 },
                displayOrder: 1,
            },
            streep_tussenruimte_1: {
                name: 'Lengte tussenruimte cm',
                type: FieldType.Number,
                validators: { min: 0 },
                displayOrder: 2,
            },
            streep_breedte_1: { name: 'Breedte cm', type: FieldType.Number, validators: { min: 0 }, displayOrder: 3 },
            streep_laagdikte_1: {
                name: 'Laagdikte mm',
                type: FieldType.Number,
                validators: { min: 0 },
                displayOrder: 4,
            },
            streep_bedekkingsgraad_1: {
                name: 'Bedekkingsgraad %',
                type: FieldType.Number,
                validators: { min: 0, max: 100 },
                displayOrder: 5,
            },
            streep_nastrooi_1: {
                name: 'Nastrooimengsel g/m²',
                type: FieldType.Number,
                validators: { min: 0 },
                displayOrder: 6,
            },
            streep_lengte_2: {
                name: 'Lengte streep cm',
                type: FieldType.Number,
                validators: { min: 0 },
                displayOrder: 7,
            },
            streep_tussenruimte_2: {
                name: 'Lengte tussenruimte cm',
                type: FieldType.Number,
                validators: { min: 0 },
                displayOrder: 8,
            },
            streep_breedte_2: { name: 'Breedte cm', type: FieldType.Number, validators: { min: 0 }, displayOrder: 9 },
            streep_laagdikte_2: {
                name: 'Laagdikte mm',
                type: FieldType.Number,
                validators: { min: 0 },
                displayOrder: 10,
            },
            streep_bedekkingsgraad_2: {
                name: 'Bedekkingsgraad %',
                type: FieldType.Number,
                validators: { min: 0, max: 100 },
                displayOrder: 11,
            },
            streep_nastrooi_2: {
                name: 'Nastrooimengsel g/m²',
                type: FieldType.Number,
                validators: { min: 0 },
                displayOrder: 12,
            },
        },
    },
    registratie_menging: {
        label: 'Registratie menging',
        fields: {
            meng_verf_1: { name: 'Verf kg', type: FieldType.Number, validators: { min: 0 }, displayOrder: 1 },
            meng_parels_1: { name: 'Parels kg', type: FieldType.Number, validators: { min: 0 }, displayOrder: 2 },
            meng_verf_2: { name: 'Verf kg', type: FieldType.Number, validators: { min: 0 }, displayOrder: 3 },
            meng_parels_2: { name: 'Parels kg', type: FieldType.Number, validators: { min: 0 }, displayOrder: 4 },
        },
    },
    maatvoering_algemeen: {
        label: 'Maatvoering algemeen',
        fields: {
            maat_goed_1: { name: 'Maatvoering goed', type: FieldType.Radio, options: jaNeeOptions, displayOrder: 1 },
            uitvoering_goed_1: {
                name: 'Uitvoering goed',
                type: FieldType.Radio,
                options: jaNeeOptions,
                displayOrder: 2,
            },
            maat_goed_2: { name: 'Maatvoering goed', type: FieldType.Radio, options: jaNeeOptions, displayOrder: 3 },
            uitvoering_goed_2: {
                name: 'Uitvoering goed',
                type: FieldType.Radio,
                options: jaNeeOptions,
                displayOrder: 4,
            },
        },
    },
};

const verkeersmaatregel = {
    displayOrder: 17,
    name: 'Verkeersmaatregel',
    type: FieldType.Select,
    disableServersideRequiredTillId: 17442,
    requiredRules: [
        {
            field: 'status_key',
            hasValue: [
                PlanningStatus.PlanningGarantiewerk,
                PlanningStatus.PlanningGeblokkeerd,
                PlanningStatus.PlanningGepland,
                PlanningStatus.PlanningRegen,
                PlanningStatus.PlanningReservering,
                PlanningStatus.PlanningSlaapdag,
                PlanningStatus.PlanningVerwerkt,
            ],
        },
    ],
    options: [
        { key: 'geen', name: 'Geen VKM' },
        { key: 'eigen-voorsein', name: 'Eigen voorsein' },
        { key: 'krans-1', name: 'KRANS 1' },
        { key: 'krans-2', name: 'KRANS 2' },
        { key: 'krans-2+1', name: 'KRANS 2+1' },
        { key: 'krans-3', name: 'KRANS 3' },
        { key: 'krans-4', name: 'KRANS 4' },
        { key: 'krans-5', name: 'KRANS 5' },
        { key: 'krans-6', name: 'KRANS 6' },
    ],
};

const contactpersoon = {
    person_id: {
        displayOrder: 18,
        name: 'Contactpersoon',
        type: FieldType.PersonId,
    },
};

export const entityTypes = {
    blast: {
        key: 'blast',
        name: 'Stralen',
        plural_name: 'Stralen',
        icon: 'fa-vacuum',
        afas_name: '',
        order: 1,
        is_team: false,
        has_realisations: false,
        has_driver: false,
        has_lowloader: true,
        has_truck: true,
        has_preferred_team: false,
        has_employees: false,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        has_entitytype_keys: ['blast_truck'],
        has_employee_roles: [Roles.EmployeeBlast],
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {
            verkeersmaatregel,
            ...contactpersoon,
        },
        fields_planning: {
            ...basePlanningFields,
        },
        fields_phe: {
            ...basePheFields,
        },
        fields_dayreport: baseDayReportFields,
        fields_phe_data: {
            behoefteM1: {
                name: 'Behoefte strekkende meter',
                type: FieldType.Number,
                validators: {
                    min: 0,
                },
            },
            behoefteM2: {
                name: 'Behoefte vierkante meter',
                type: FieldType.Number,
                validators: {
                    min: 0,
                },
            },
        },
    },
    markings: {
        key: 'markings',
        name: 'Markeren',
        plural_name: 'Markeren',
        icon: 'fa-road',
        afas_name: '',
        order: 2,
        is_team: false,
        has_realisations: false,
        has_driver: false,
        has_lowloader: true,
        has_truck: true,
        has_preferred_team: false,
        has_employees: false,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        has_entitytype_keys: [
            'truck',
            'wiper',
            'other_marking_units',
            'figuration_unit',
            'thermo_zelfbouw',
            'trailers',
            'semi_trailer',
            'other_vehicle',
        ],
        has_employee_roles: [Roles.EmployeeMarkings],
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {
            verkeersmaatregel,
            ...contactpersoon,
            behoefteInventarisatie: {
                name: 'Dagen inventarisatie',
                type: FieldType.Number,
                default: 0,
            },
            mallen: {
                name: 'Mallen',
                type: FieldType.Checkbox,
                default: false,
            },
            opmeten: {
                name: 'Opmeten',
                type: FieldType.Checkbox,
                default: true,
            },
            uitzetten: {
                name: 'Uitzetten',
                type: FieldType.Checkbox,
                default: true,
            },
        },
        fields_planning: {
            ...basePlanningFields,
        },
        fields_phe: {
            ...basePheFields,
        },
        fields_dayreport: baseDayReportFields,
        fields_phe_data: {
            behoefteThermoM1: {
                name: 'Behoefte thermo strekkende meter',
                type: FieldType.Number,
                validators: {
                    min: 0,
                },
            },
            behoefteThermoM2: {
                name: 'Behoefte thermo vierkante meter',
                type: FieldType.Number,
                validators: {
                    min: 0,
                },
            },
            behoefteOverigM1: {
                name: 'Behoefte overig strekkende meter',
                type: FieldType.Number,
                validators: {
                    min: 0,
                },
            },
            behoefteOverigM2: {
                name: 'Behoefte overig vierkante meter',
                type: FieldType.Number,
                validators: {
                    min: 0,
                },
            },
            behoefteUren: {
                name: 'Behoefte uren',
                type: FieldType.Number,
                validators: {
                    min: 0,
                    max: 999,
                },
            },
            materiaal: {
                name: 'Materiaal',
                type: FieldType.Text,
            },
        },
    },
    wipe: {
        key: 'wipe',
        name: 'Vegen',
        plural_name: 'Vegen',
        icon: 'fa-broom-wide',
        afas_name: '',
        order: 3,
        is_team: false,
        has_realisations: false,
        has_driver: false,
        has_lowloader: true,
        has_truck: true,
        has_preferred_team: false,
        has_employees: false,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        has_entitytype_keys: ['wiper'],
        has_employee_roles: [Roles.EmployeeWipe],
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {
            verkeersmaatregel,
            ...contactpersoon,
        },
        fields_planning: {
            ...basePlanningFields,
        },
        fields_phe: basePheFields,
        fields_dayreport: baseDayReportFields,
        fields_phe_data: {
            behoefteUren: {
                name: 'Behoefte uren',
                type: FieldType.Number,
                validators: {
                    min: 0,
                    max: 999,
                },
            },
        },
    },
    inventorize: {
        key: 'inventorize',
        name: 'Inventariseren',
        plural_name: 'Inventariseren',
        icon: 'fa-video',
        afas_name: '',
        order: 4,
        is_team: false,
        has_realisations: false,
        has_driver: false,
        has_lowloader: true,
        has_truck: true,
        has_preferred_team: false,
        has_employees: false,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        has_entitytype_keys: ['other_vehicle'],
        has_employee_roles: [Roles.EmployeeInventorize],
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: {
            ...basePlanningFields,
        },
        fields_phe: basePheFields,
        fields_dayreport: baseDayReportFields,
        fields_phe_data: {
            behoefteUren: {
                name: 'Behoefte uren',
                type: FieldType.Number,
                validators: {
                    min: 0,
                    max: 999,
                },
            },
        },
    },
    workplace: {
        key: 'workplace',
        name: 'Werkplaats',
        plural_name: 'Werkplaats',
        icon: 'fa-garage-car',
        afas_name: '',
        order: 4,
        is_team: false,
        has_realisations: false,
        has_driver: false,
        has_lowloader: true,
        has_truck: true,
        has_preferred_team: false,
        has_employees: false,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        has_entitytype_keys: [
            'barrier_vehicle',
            'blast_truck',
            'figuration_unit',
            'other_marking_units',
            'other_vehicle',
            'semi_trailer',
            'thermo_zelfbouw',
            'trailers',
            'truck',
            'wiper',
        ],
        has_employee_roles: [Roles.EmployeeInventorize],
        may_edit_own_roles: [Roles.Projectmanager, Roles.Foreman],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: {
            ...basePlanningFields,
        },
        fields_phe: basePheFields,
        fields_dayreport: baseDayReportFields,
        fields_phe_data: {
            behoefteUren: {
                name: 'Behoefte uren',
                type: FieldType.Number,
                validators: {
                    min: 0,
                    max: 999,
                },
            },
        },
    },
    wiper: {
        key: 'wiper',
        name: 'Veger',
        plural_name: 'Vegers',
        icon: 'fa-broom-wide',
        afas_name: 'Veegwagen',
        order: 5,
        is_team: false,
        has_realisations: true,
        has_driver: true,
        has_lowloader: true,
        has_truck: true,
        has_preferred_team: false,
        has_employees: false,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: basePlanningFields,
        fields_phe: {
            ...basePheFields,
            driver_user_id: {
                name: 'Chauffeur',
                type: FieldType.UserIdWithLeaves,
                asyncValidators: [
                    {
                        type: 'localUserIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: [
                    Roles.EmployeeBlast,
                    Roles.EmployeeMarkings,
                    Roles.EmployeeWipe,
                    Roles.EmployeeInventorize,
                ],
            },
            entity_id: {
                name: 'Veger',
                type: FieldType.EntityId,
                extraFields: ['hiring_id', 'name'],
                asyncValidators: [
                    {
                        type: 'localEntityIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: ['wiper'],
            },
            comment: {
                displayOrder: 8,
                displayPercent: 20,
                name: 'Opmerking',
                type: FieldType.Textarea,
                validators: {
                    maxLength: 3000,
                },
            },
        },
        fields_phe_data: {},
    },
    truck: {
        key: 'truck',
        name: 'Vrachtwagen',
        plural_name: 'Vrachtwagens',
        icon: 'fa-truck',
        afas_name: 'Vrachtwagen',
        order: 6,
        is_team: false,
        has_realisations: true,
        has_driver: true,
        has_lowloader: false,
        has_truck: false,
        has_preferred_team: false,
        has_employees: true,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: basePlanningFields,
        fields_phe: {
            ...basePheFields,
            driver_user_id: {
                name: 'Chauffeur',
                displayOrder: 7,
                type: FieldType.UserIdWithLeaves,
                required: true,
                asyncValidators: [
                    {
                        type: 'localUserIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: [
                    Roles.EmployeeBlast,
                    Roles.EmployeeMarkings,
                    Roles.EmployeeWipe,
                    Roles.EmployeeInventorize,
                ],
            },
            entity_id: {
                name: 'Vrachtwagen',
                displayOrder: 8,
                type: FieldType.EntityId,
                extraFields: ['hiring_id', 'name'],
                required: true,
                asyncValidators: [
                    {
                        type: 'localEntityIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: ['truck'],
            },
            comment: {
                displayOrder: 9,
                displayPercent: 20,
                name: 'Opmerking',
                type: FieldType.Textarea,
                validators: { maxLength: 3000 },
            },
        },
        fields_phe_data: {},
    },
    other_marking_units: {
        key: 'other_marking_units',
        name: 'Overige markeringsunits',
        plural_name: 'Overige markeringsunits',
        icon: 'fa-truck',
        afas_name: 'Overige markeringsunits',
        order: 7,
        is_team: false,
        has_realisations: true,
        has_driver: true,
        has_lowloader: false,
        has_truck: false,
        has_preferred_team: false,
        has_employees: true,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: basePlanningFields,
        fields_phe: {
            ...basePheFields,
            driver_user_id: {
                name: 'Chauffeur',
                type: FieldType.UserIdWithLeaves,
                asyncValidators: [
                    {
                        type: 'localUserIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: [
                    Roles.EmployeeBlast,
                    Roles.EmployeeMarkings,
                    Roles.EmployeeWipe,
                    Roles.EmployeeInventorize,
                ],
            },
            entity_id: {
                name: 'Overige markeringsunits',
                type: FieldType.EntityId,
                extraFields: ['hiring_id', 'name'],
                asyncValidators: [
                    {
                        type: 'localEntityIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: ['other_marking_units'],
            },
            comment: {
                displayOrder: 8,
                displayPercent: 20,
                name: 'Opmerking',
                type: FieldType.Textarea,
                validators: {
                    maxLength: 3000,
                },
            },
        },
        fields_phe_data: {},
    },
    figuration_unit: {
        key: 'figuration_unit',
        name: 'Figuratie unit',
        plural_name: 'Figuratie units',
        icon: 'fa-truck',
        afas_name: 'Figuratie unit',
        order: 8,
        is_team: false,
        has_realisations: true,
        has_driver: true,
        has_lowloader: false,
        has_truck: false,
        has_preferred_team: false,
        has_employees: true,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: basePlanningFields,
        fields_phe: {
            ...basePheFields,
            driver_user_id: {
                name: 'Chauffeur',
                type: FieldType.UserIdWithLeaves,
                asyncValidators: [
                    {
                        type: 'localUserIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: [
                    Roles.EmployeeBlast,
                    Roles.EmployeeMarkings,
                    Roles.EmployeeWipe,
                    Roles.EmployeeInventorize,
                ],
            },
            entity_id: {
                name: 'Figuratie unit',
                type: FieldType.EntityId,
                extraFields: ['hiring_id', 'name'],
                asyncValidators: [
                    {
                        type: 'localEntityIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: ['figuration_unit'],
            },
            comment: {
                displayOrder: 8,
                displayPercent: 20,
                name: 'Opmerking',
                type: FieldType.Textarea,
                validators: {
                    maxLength: 3000,
                },
            },
        },
        fields_phe_data: {},
    },
    blast_truck: {
        key: 'blast_truck',
        name: 'Straalwagen',
        plural_name: 'Straalwagens',
        icon: 'fa-truck',
        afas_name: 'Straalwagen',
        order: 9,
        is_team: false,
        has_realisations: true,
        has_driver: true,
        has_lowloader: false,
        has_truck: false,
        has_preferred_team: false,
        has_employees: true,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: basePlanningFields,
        fields_phe: {
            ...basePheFields,
            driver_user_id: {
                name: 'Chauffeur',
                type: FieldType.UserIdWithLeaves,
                asyncValidators: [
                    {
                        type: 'localUserIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: [
                    Roles.EmployeeBlast,
                    Roles.EmployeeMarkings,
                    Roles.EmployeeWipe,
                    Roles.EmployeeInventorize,
                ],
            },
            entity_id: {
                name: 'Straalwagen',
                type: FieldType.EntityId,
                extraFields: ['hiring_id', 'name'],
                asyncValidators: [
                    {
                        type: 'localEntityIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: ['blast_truck'],
            },
            comment: {
                displayOrder: 8,
                displayPercent: 20,
                name: 'Opmerking',
                type: FieldType.Textarea,
                validators: {
                    maxLength: 3000,
                },
            },
        },
        fields_phe_data: {},
    },
    barrier_vehicle: {
        key: 'barrier_vehicle',
        name: 'Afzettingsvoertuig',
        plural_name: 'Afzettingsvoertuigen',
        icon: 'fa-truck',
        afas_name: 'Afzettingsvoertuig',
        order: 10,
        is_team: false,
        has_realisations: true,
        has_driver: true,
        has_lowloader: false,
        has_truck: false,
        has_preferred_team: false,
        has_employees: true,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: basePlanningFields,
        fields_phe: {
            ...basePheFields,
            driver_user_id: {
                name: 'Chauffeur',
                type: FieldType.UserIdWithLeaves,
                asyncValidators: [
                    {
                        type: 'localUserIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: [
                    Roles.EmployeeBlast,
                    Roles.EmployeeMarkings,
                    Roles.EmployeeWipe,
                    Roles.EmployeeInventorize,
                ],
            },
            entity_id: {
                name: 'Afzettingsvoertuig',
                type: FieldType.EntityId,
                extraFields: ['hiring_id', 'name'],
                asyncValidators: [
                    {
                        type: 'localEntityIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: ['barrier_vehicle'],
            },
            comment: {
                displayOrder: 8,
                displayPercent: 20,
                name: 'Opmerking',
                type: FieldType.Textarea,
                validators: {
                    maxLength: 3000,
                },
            },
        },
        fields_phe_data: {},
    },
    thermo_zelfbouw: {
        key: 'thermo_zelfbouw',
        name: 'Thermo zelfbouw',
        plural_name: 'Thermo zelfbouw',
        icon: 'fa-truck',
        afas_name: 'Thermo zelfbouw',
        order: 11,
        is_team: false,
        has_realisations: true,
        has_driver: true,
        has_lowloader: false,
        has_truck: false,
        has_preferred_team: false,
        has_employees: true,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: basePlanningFields,
        fields_phe: {
            ...basePheFields,
            driver_user_id: {
                name: 'Chauffeur',
                type: FieldType.UserIdWithLeaves,
                asyncValidators: [
                    {
                        type: 'localUserIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: [
                    Roles.EmployeeBlast,
                    Roles.EmployeeMarkings,
                    Roles.EmployeeWipe,
                    Roles.EmployeeInventorize,
                ],
            },
            entity_id: {
                name: 'Thermo zelfbouw',
                type: FieldType.EntityId,
                extraFields: ['hiring_id', 'name'],
                asyncValidators: [
                    {
                        type: 'localEntityIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: ['thermo_zelfbouw'],
            },
            comment: {
                displayOrder: 8,
                displayPercent: 20,
                name: 'Opmerking',
                type: FieldType.Textarea,
                validators: {
                    maxLength: 3000,
                },
            },
        },
        fields_phe_data: {},
    },
    trailers: {
        key: 'trailers',
        name: 'Aanhangers',
        plural_name: 'Aanhangers',
        icon: 'fa-truck',
        afas_name: 'Aanhangers',
        order: 12,
        is_team: false,
        has_realisations: true,
        has_driver: true,
        has_lowloader: false,
        has_truck: false,
        has_preferred_team: false,
        has_employees: true,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: basePlanningFields,
        fields_phe: {
            ...basePheFields,
            driver_user_id: {
                name: 'Chauffeur',
                type: FieldType.UserIdWithLeaves,
                asyncValidators: [
                    {
                        type: 'localUserIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: [
                    Roles.EmployeeBlast,
                    Roles.EmployeeMarkings,
                    Roles.EmployeeWipe,
                    Roles.EmployeeInventorize,
                ],
            },
            entity_id: {
                name: 'Aanhangers',
                type: FieldType.EntityId,
                extraFields: ['hiring_id', 'name'],
                asyncValidators: [
                    {
                        type: 'localEntityIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: ['trailers'],
            },
            comment: {
                displayOrder: 8,
                displayPercent: 20,
                name: 'Opmerking',
                type: FieldType.Textarea,
                validators: {
                    maxLength: 3000,
                },
            },
        },
        fields_phe_data: {},
    },
    semi_trailer: {
        key: 'semi_trailer',
        name: 'Oplegger',
        plural_name: 'Opleggers',
        icon: 'fa-truck',
        afas_name: 'Oplegger',
        order: 13,
        is_team: false,
        has_realisations: true,
        has_driver: true,
        has_lowloader: false,
        has_truck: false,
        has_preferred_team: false,
        has_employees: true,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: basePlanningFields,
        fields_phe: {
            ...basePheFields,
            driver_user_id: {
                name: 'Chauffeur',
                type: FieldType.UserIdWithLeaves,
                asyncValidators: [
                    {
                        type: 'localUserIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: [
                    Roles.EmployeeBlast,
                    Roles.EmployeeMarkings,
                    Roles.EmployeeWipe,
                    Roles.EmployeeInventorize,
                ],
            },
            entity_id: {
                name: 'Oplegger',
                type: FieldType.EntityId,
                extraFields: ['hiring_id', 'name'],
                asyncValidators: [
                    {
                        type: 'localEntityIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: ['semi_trailer'],
            },
            comment: {
                displayOrder: 8,
                displayPercent: 20,
                name: 'Opmerking',
                type: FieldType.Textarea,
                validators: {
                    maxLength: 3000,
                },
            },
        },
        fields_phe_data: {},
    },
    other_vehicle: {
        key: 'other_vehicle',
        name: 'Overig voertuig',
        plural_name: 'Overige voertuigen',
        icon: 'fa-truck',
        afas_name: 'Overig voertuig',
        order: 14,
        is_team: false,
        has_realisations: true,
        has_driver: true,
        has_lowloader: false,
        has_truck: false,
        has_preferred_team: false,
        has_employees: true,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        may_edit_own_roles: [Roles.Projectmanager],
        may_edit_roles: [Roles.Planner],
        force_wide: false,
        visible_overview: false,
        visible_transport: true,
        visible_small: false,
        belongs_to: null,
        fields_planning_data: {},
        fields_planning: basePlanningFields,
        fields_phe: {
            ...basePheFields,
            driver_user_id: {
                name: 'Chauffeur',
                type: FieldType.UserIdWithLeaves,
                asyncValidators: [
                    {
                        type: 'localUserIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: [
                    Roles.EmployeeBlast,
                    Roles.EmployeeMarkings,
                    Roles.EmployeeWipe,
                    Roles.EmployeeInventorize,
                ],
            },
            entity_id: {
                name: 'Overig voertuig',
                type: FieldType.EntityId,
                extraFields: ['hiring_id', 'name'],
                asyncValidators: [
                    {
                        type: 'localEntityIsAvailable',
                        config: {
                            beginDateField: 'begindate',
                            endDateField: 'enddate',
                        },
                    },
                ],
                dataFilter: ['other_vehicle'],
            },
            comment: {
                displayOrder: 8,
                displayPercent: 20,
                name: 'Opmerking',
                type: FieldType.Textarea,
                validators: {
                    maxLength: 3000,
                },
            },
        },
        fields_phe_data: {},
    },
    hiringStaff: {
        key: 'hiringStaff',
        name: 'Inhuur personeel',
        plural_name: 'Inhuur personeel',
        icon: null,
        afas_name: null,
        order: 100,
        is_team: false,
        has_realisations: true,
        has_driver: false,
        has_lowloader: false,
        has_truck: false,
        has_preferred_team: false,
        has_employees: false,
        has_default_performer: false,
        has_default_asphalt_performer: false,
        force_wide: false,
        visible_overview: false,
        visible_transport: false,
        visible_small: true,
        belongs_to: null,
        fields_planning: {},
        fields_planning_data: {},
        fields_phe: {
            hiring_id: {
                displayOrder: 3,
                displayPercent: 20,
                name: 'Inhuur',
                type: FieldType.Hiring,
                dataFilter: ['hiringStaff'],
            },
            name: {
                displayOrder: 4,
                displayPercent: 20,
                name: 'Medewerker',
                extraFields: ['entity_id'],
                type: FieldType.Textarea,
                validators: {
                    maxLength: 200,
                },
            },
            ...basePheFields,
            empty: {
                displayOrder: 8,
                displayPercent: 20,
                name: 'Route',
                type: FieldType.Empty,
            },
            comment: {
                displayOrder: 9,
                displayPercent: 20,
                name: 'Omschrijving',
                type: FieldType.Textarea,
                validators: {
                    maxLength: 200,
                },
            },
        },
        fields_phe_data: {},
        multi_select: false,
    },
} satisfies Record<string, EntityType>;
